import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useApprovals } from '../api/hooks';
import { ACTION_LABELS } from '../api/types';
import { Role } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card, CardHeader } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { EmptyState, QueryBoundary } from '../components/states';
import { ActionStatusBadge, ModeBadge } from '../components/DomainBadges';
import { useFormatters } from '../time/format';

/**
 * The approvals queue.
 *
 * Sorted by expiry, soonest first. An approval stands for `approvalTtlHours` and then expires
 * and escalates rather than disappearing, so the one closest to expiring is the one that
 * needs a decision — not the most recently created.
 *
 * Approving happens on the case page rather than here: an approver needs the verdict, the
 * evidence coverage and the other alerts in front of them, and a queue row cannot carry that.
 * Batch-approving containment from a list is the exact affordance this platform should not
 * offer.
 */
export function Approvals() {
  const principal = usePrincipal();
  const query = useApprovals();
  const { precise, relative, daysUntil } = useFormatters();

  const rows = useMemo(() => {
    const actions = [...(query.data?.actions ?? [])];
    actions.sort((a, b) => {
      // Actions with no expiry sort last: they are not on a clock.
      if (!a.expires_at && !b.expires_at) return a.created_at.localeCompare(b.created_at);
      if (!a.expires_at) return 1;
      if (!b.expires_at) return -1;
      return a.expires_at.localeCompare(b.expires_at);
    });
    return actions;
  }, [query.data]);

  return (
    <>
      <PageHeader
        title="Approvals"
        subtitle="Containment the policy gate routed to a human. Soonest to expire first."
      />

      {principal.role < Role.RESPONDER ? (
        <div className="mb-4">
          <Callout tone="neutral">
            You can see this queue but not act on it — approving containment requires the
            IRA.Responder role.
          </Callout>
        </div>
      ) : null}

      <QueryBoundary
        isLoading={query.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Loading approvals"
      >
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              icon={CheckCircleIcon}
              title="Nothing waiting for approval"
              detail="Actions appear here the moment the policy gate routes one to a human."
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {rows.map((action) => {
              const days = daysUntil(action.expires_at);
              const urgent = days !== null && days <= 0;
              return (
                <Card key={action.action_id} tone={urgent ? 'warning' : undefined}>
                  <CardHeader
                    title={ACTION_LABELS[action.kind] ?? action.kind}
                    subtitle={action.target}
                    action={
                      <>
                        <ModeBadge mode={action.mode} />
                        <ActionStatusBadge status={action.status} />
                      </>
                    }
                  />
                  {action.rationale ? (
                    <p className="mb-2 text-sm text-muted">{action.rationale}</p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-subtle">
                    <Link
                      to={`/cases/${encodeURIComponent(action.case_id)}`}
                      className="font-mono text-brand-text hover:underline"
                    >
                      {action.case_id}
                    </Link>
                    {action.expires_at ? (
                      <span className={urgent ? 'font-semibold text-warning-fg' : ''}>
                        Expires {relative(action.expires_at)} · {precise(action.expires_at)}
                      </span>
                    ) : null}
                    {action.requested_by ? (
                      <span>Requested by {action.requested_by}</span>
                    ) : null}
                  </div>
                  <div className="mt-3">
                    <Link to={`/cases/${encodeURIComponent(action.case_id)}`}>
                      <Button variant="primary" size="xs">
                        Review case and decide
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
